// Global imports
import {
  formatDate,
  formatPrice,
  getCurrencySymbol,
  toFixedNumber,
} from '~/utils'
import { getMaxQtyFallback, capStock } from '~/utils/stock'

// Types
import type { LunarCart, CustomerAddressProps } from '~/types'
import type { BrendaChargesResult } from '~/types/brendaCharges'
import type {
  LunarBasket,
  BasketLineData,
  BasketLineGrouped,
  BasketRawLine,
  BasketIncludedItem,
  BasketProductVariant,
  BasketMedia,
  BasketLineItemProps,
  BasketLineMeta,
  BasketRevalidation,
  BasketRevalidationIssue,
} from '~/types/basket'

// ---------------------------------------------------------------------------
// Included resource helpers
// ---------------------------------------------------------------------------

/**
 * Resolves the product name for a cart line from its purchasable variant.
 */
export const getIncludedName = (
  variantId: string | undefined,
  included: BasketIncludedItem[]
): string => {
  if (!variantId) return ''

  const variant = included.find(
    (e): e is BasketProductVariant =>
      e.type === 'product-variants' && e.id === variantId
  )

  return variant?.attributes?.name?.en ?? ''
}

/**
 * Resolves the primary image URL for a cart line from its purchasable variant.
 */
export const getIncludedImage = (
  variantId: string | undefined,
  included: BasketIncludedItem[]
): string | null => {
  if (!variantId) return null

  const variant = included.find(
    (e): e is BasketProductVariant =>
      e.type === 'product-variants' && e.id === variantId
  )

  const imageId = variant?.relationships?.images?.data?.[0]?.id
  if (!imageId) return null

  const media = included.find(
    (e): e is BasketMedia => e.type === 'media' && e.id === imageId
  )

  return media?.attributes?.url ?? null
}

// ---------------------------------------------------------------------------
// Cart line building
// ---------------------------------------------------------------------------

/**
 * Processes cart.included into fully shaped BasketLine objects with
 * resolved title and imageUrl from the included relationships.
 */
export const buildCartLines = (
  included: BasketIncludedItem[] | null | undefined
): BasketLineData[] => {
  if (!included?.length) return []

  // Filter to only cart-line entries from the included array
  const cartLines = included.filter(
    (e): e is BasketRawLine => e?.type === 'cart-lines'
  )

  return cartLines.map(line => ({
    ...line,
    // Create fresh nested refs so we never mutate the loader data
    attributes: {
      ...line.attributes,
      meta: { ...line.attributes.meta },
      prices: { ...line.attributes.prices },
    },
    title: getIncludedName(line.relationships?.purchasable?.data?.id, included),
    imageUrl: getIncludedImage(
      line.relationships?.purchasable?.data?.id,
      included
    ),
  }))
}

// Returns the grouping ID for a parent line
export const getLineDisplayGroupId = (meta?: BasketLineMeta) => {
  return meta?.line_group_id?.toString() ?? undefined
}

// Returns the parent grouping ID for a child line
export const getLineDisplayParentGroupId = (meta?: BasketLineMeta) => {
  return meta?.line_parent_group_id?.toString() ?? undefined
}

// Returns the extras heading based on product type
export const getExtrasHeading = (productType?: string) => {
  return productType === 'consumable' ? 'Buy extras' : 'Hire extras'
}

/**
 * Groups flat cart lines into parent + linked (add-on) pairs.
 * Groups by line_group_id / line_parent_group_id.
 */
export const groupCartLines = (
  lines: BasketLineData[]
): BasketLineGrouped[] => {
  const parents = lines.filter(
    e => !getLineDisplayParentGroupId(e?.attributes?.meta)
  )
  const children = lines.filter(
    e => !!getLineDisplayParentGroupId(e?.attributes?.meta)
  )

  return parents.map(parent => {
    const parentGroupId = getLineDisplayGroupId(parent?.attributes?.meta)

    return {
      parent,
      linked: parentGroupId
        ? children.filter(
            child =>
              getLineDisplayParentGroupId(child?.attributes?.meta) ===
              parentGroupId
          )
        : [],
    }
  })
}

/**
 * Returns the total count of individual cart line items (not groups).
 */
export const getCartItemsCount = (lines: BasketLineData[]): number =>
  lines.length

/**
 * Returns true if every delivery item in the cart has a zero delivery price.
 */
export const getCartHasFreeDelivery = (
  groups: BasketLineGrouped[]
): boolean => {
  if (!groups.length) return false

  // Include all items that involve delivery — explicit delivery fulfilment,
  // or items without a fulfilment set (buy products default to delivery)
  const deliveryItems = groups.filter(({ parent }) => {
    const fulfilment = parent?.attributes?.meta?.selected_fulfilment

    return fulfilment === 'delivery' || !fulfilment
  })

  if (!deliveryItems.length) return false

  // All delivery items must have a zero delivery price
  return deliveryItems.every(
    ({ parent }) => Number(parent?.attributes?.meta?.delivery_price ?? -1) === 0
  )
}

// Re-export stock utils for consumers that import from ~/utils/basket
export { getMaxQtyFallback, capStock }

/**
 * Returns the max available quantity for a cart line.
 */
export const getLineAvailableQty = (
  attributes?: BasketRawLine['attributes']
): number => {
  const fallback = getMaxQtyFallback(attributes?.meta?.product_type)

  return capStock(attributes?.meta?.max_quantity, fallback)
}

/**
 * Returns the linked children of a parent basket line by matching
 * `line_parent_group_id` against the parent's display group id. Used by
 * update flows that need to feed `buildUpdatesFromResolved` with the list
 * of linked items to refresh alongside the parent.
 */
export const getLinkedLines = (
  parent: BasketLineData,
  allLines: BasketLineData[]
): BasketLineData[] => {
  const parentGroupId = getLineDisplayGroupId(parent.attributes.meta)

  if (!parentGroupId) return []

  return allLines.filter(
    l => getLineDisplayParentGroupId(l.attributes.meta) === parentGroupId
  )
}

/**
 * Finds linked lines for a parent and builds update payloads with a new max_quantity.
 * The stock resolver receives each child line and returns the raw stock value
 * (e.g. from delivery addon data or pickup branch availability).
 * Only returns updates where max_quantity actually changed.
 */
export const buildLinkedStockUpdates = (
  parentMeta: BasketLineData['attributes']['meta'],
  allLines: BasketLineData[],
  resolveStock: (child: BasketLineData) => number | null | undefined
): BasketLineItemProps[] => {
  const parentGroupId = getLineDisplayGroupId(parentMeta)

  if (!parentGroupId) return []

  const linkedLines = allLines.filter(
    l => getLineDisplayParentGroupId(l.attributes?.meta) === parentGroupId
  )

  return linkedLines
    .map(child => {
      const childFallback = getMaxQtyFallback(
        child.attributes.meta?.product_type
      )
      const nextMaxQty = capStock(resolveStock(child), childFallback)
      const currentMaxQty = Number(child.attributes.meta?.max_quantity)

      if (Number.isFinite(currentMaxQty) && currentMaxQty === nextMaxQty)
        return null

      return {
        id: child.id,
        quantity: child.attributes.quantity,
        stock_code: child.attributes.stock_code,
        meta: {
          ...child.attributes.meta,
          max_quantity: nextMaxQty,
        },
      }
    })
    .filter(u => u !== null) as BasketLineItemProps[]
}

/**
 * Builds a singular line update
 */
export const buildLineUpdate = (
  line: BasketLineData,
  metaOverrides: Partial<BasketLineData['attributes']['meta']>,
  quantity?: number
): BasketLineItemProps => {
  const lineUpdate: BasketLineItemProps = {
    id: line.id,
    quantity: quantity ?? line.attributes.quantity,
    stock_code: line.attributes.stock_code,
    meta: {
      ...line.attributes.meta,
      ...metaOverrides,
    },
  }

  return lineUpdate
}

// ---------------------------------------------------------------------------
// `BrendaChargesResult` → basket line updates
// ---------------------------------------------------------------------------
//
// Basket flows that own a `useBrendaCharges` result use these helpers
// to translate it into a dispatch-ready `[parent, ...linked]` update
// payload. The hook already did all the stock / date / branch work
// inside `evaluateBrendaCharges`, so there are no resolvers here —
// we're just reading the delivery / pickup / product summaries off
// `charges` and writing them onto basket meta.

interface BuildBasketUpdatesFromChargesOptions {
  /** The fulfilment the parent line should commit to after this update. */
  fulfilment: 'delivery' | 'pickup'
  /**
   * New parent quantity. Defaults to the existing line's `quantity` —
   * pass an explicit value when the flow is changing qty.
   */
  parentQuantity?: number
  /**
   * Flow-specific meta overrides merged onto the parent *last*, so
   * callers can override any field derived from `charges`. Example:
   * the dates modal passes `{ hire_start_date, hire_end_date }` here
   * because committing new dates is the whole point of that flow.
   */
  extraParentMeta?: Partial<BasketLineMeta>
}

/**
 * Builds the update for a single linked child line from its parent's
 * live charges result.
 *
 * Per-child rules:
 *
 *   - **Quantity**: mandatory children track the parent's new quantity;
 *     optional children keep their own current quantity.
 *   - **No match in charges.product.addOns**: preserve the child's
 *     existing meta wholesale so an optional add-on that wasn't
 *     repriced this round doesn't get silently cleared.
 *   - **Delivery stock / date / price**: read off the add-on's own
 *     `delivery` summary (`charges.product.addOns[].delivery`).
 *   - **Pickup stock**: read off the *parent's chosen branch* via
 *     `charges.pickup.linked`, not the add-on's own delivery summary.
 *     Linked pickup stock is always a per-branch lookup because two
 *     branches can have very different stock of the same add-on.
 */
const buildLinkedUpdateFromCharges = (
  child: BasketLineData,
  charges: BrendaChargesResult,
  parentFulfilment: 'delivery' | 'pickup',
  parentQuantity: number
): BasketLineItemProps => {
  const meta = child.attributes.meta
  const isMandatory = !!meta?.is_mandatory
  const effectiveChildQty = isMandatory
    ? parentQuantity
    : child.attributes.quantity

  const match = charges.product.addOns.find(
    addOn => addOn.stockCode === child.attributes.stock_code
  )

  // Not in the latest charges — don't touch meta, just sync the qty.
  if (!match) {
    return buildLineUpdate(child, {}, effectiveChildQty)
  }

  // Delivery summary comes with the add-on; pickup stock has to be
  // looked up on the parent's chosen branch (same-coded add-ons can
  // differ across branches).
  const deliveryAvailable = match.delivery.available
  const deliveryMax = deliveryAvailable ? match.delivery.maxQuantity : 0

  const pickupLinkedMatch = charges.pickup.linked.find(
    l => l.stockCode === child.attributes.stock_code
  )
  const pickupMax = pickupLinkedMatch?.maxQuantity ?? 0

  // `max_quantity` on the line reflects the side the *parent* is
  // committed to — pickupMax is already 0 when pickup isn't viable,
  // so no extra override needed.
  const maxStock = parentFulfilment === 'pickup' ? pickupMax : deliveryMax

  // `hire_damage_waiver` only applies to hire (bulk) children —
  // consumables never carry it even if the API surfaces a value.
  const isBulk = meta?.product_type === 'bulk'

  // Mandatory add-ons: Brenda returns values already scaled to
  // parent quantity. Non-mandatory: values are per-unit, multiply
  // by the child's own quantity to match what's stored on the line.
  const qtyMultiplier = isMandatory ? 1 : effectiveChildQty

  return buildLineUpdate(
    child,
    {
      price: toFixedNumber(match.price.amount * qtyMultiplier, 2),
      max_quantity: maxStock,
      ...(isBulk && {
        hire_damage_waiver: toFixedNumber(match.damageWaiver * qtyMultiplier, 2),
      }),
      ...(isBulk && match.deposit?.amount !== undefined && {
        hire_deposit: toFixedNumber(match.deposit.amount * qtyMultiplier, 2),
      }),
      // Null delivery fields when the add-on has no delivery path —
      // Lunar strips them from meta on update.
      delivery_date: deliveryAvailable ? match.delivery.date : null,
      delivery_price: deliveryAvailable
        ? toFixedNumber((match.delivery.price?.amount ?? 0) * qtyMultiplier, 2)
        : null,
    },
    effectiveChildQty
  )
}

/**
 * Translates a `BrendaChargesResult` into a `[parent, ...linked]`
 * basket update payload. Reads the already-evaluated delivery / pickup
 * / product fields off `charges` and writes them onto meta — no
 * resolvers, no stock re-checking, no branch picking.
 *
 * The parent's `max_quantity`, `delivery_*` and `pickup_*` fields all
 * reflect the `fulfilment` the caller committed to. Null-ing out
 * pickup meta when the chosen branch didn't resolve (no id / address /
 * date) is intentional: it stops stale branch data from leaking onto
 * the line when pickup becomes unreachable.
 */
export const buildBasketUpdatesFromCharges = (
  line: BasketLineData,
  linked: BasketLineData[],
  charges: BrendaChargesResult,
  options: BuildBasketUpdatesFromChargesOptions
): BasketLineItemProps[] => {
  const { fulfilment, parentQuantity, extraParentMeta = {} } = options
  const { delivery, pickup, product } = charges

  const effectiveParentQty = parentQuantity ?? line.attributes.quantity

  // Pickup is only a valid path when the branch, address and date
  // all resolved cleanly. When any of those are missing we clear the
  // pickup_* fields so the line isn't left pointing at a stale branch.
  const hasValidPickup =
    pickup.available && pickup.branchId && pickup.branchAddress && pickup.date

  const parentMaxStock =
    fulfilment === 'pickup' ? pickup.maxQuantity : delivery.maxQuantity

  // `hire_deposit` / `hire_damage_waiver` only apply to hire (bulk)
  // lines — consumables never carry them even if the API surfaces a
  // value.
  const isBulk = line.attributes.meta?.product_type === 'bulk'

  const parentUpdate = buildLineUpdate(
    line,
    {
      price: product.price?.amount ?? line.attributes.meta.price,
      max_quantity: parentMaxStock,
      ...(isBulk && {
        hire_deposit: product.deposit?.amount ?? 0,
        hire_damage_waiver: product.damageWaiver,
      }),
      // Hire window — write the *resolved* dates from the charges
      // request so `hire_start_date` / `hire_end_date` track Brenda's
      // actual window for the line. Without this, a qty change that
      // bumps the parent's pickup/delivery date forward leaves the
      // hire dates stale, which (a) lets the next loader-driven
      // revalidation anchor on the wrong window when it re-issues a
      // charges POST, and (b) leaves the line's "hire from / to"
      // fields out of sync with the fulfilment date the user sees.
      // Mirrors the same write done in
      // `buildBasketFulfilmentSwitchUpdates`.
      ...(isBulk && {
        hire_start_date: charges.request.resolvedStartDate,
        hire_end_date: charges.request.resolvedEndDate,
      }),
      // Delivery fields collapse to null when delivery isn't viable —
      // Lunar strips them from the line meta on update.
      delivery_date: delivery.available ? delivery.date : null,
      delivery_price: delivery.available
        ? (delivery.price?.amount ?? 0)
        : null,
      // Pickup fields: write the resolved branch + date when valid,
      // null them out otherwise so stale data doesn't persist.
      ...(hasValidPickup
        ? {
            pickup_branch_id: pickup.branchId,
            pickup_branch_address: pickup.branchAddress,
            pickup_branch_distance: pickup.branchDistance,
            pickup_date: pickup.date,
          }
        : {
            pickup_branch_id: null,
            pickup_branch_address: null,
            pickup_branch_distance: null,
            pickup_date: null,
          }),
      // Flow-specific overrides last — callers always win.
      ...extraParentMeta,
    },
    effectiveParentQty
  )

  const linkedUpdates = linked.map(child =>
    buildLinkedUpdateFromCharges(child, charges, fulfilment, effectiveParentQty)
  )

  return [parentUpdate, ...linkedUpdates]
}

// ---------------------------------------------------------------------------
// Fulfilment switch updates
// ---------------------------------------------------------------------------
//
// When the user toggles a line between delivery and pickup, we only
// want to rewrite the *target* side's meta fields. The non-target
// side's meta (delivery_* or pickup_*) is left intact on the line so
// the user can flip back without losing previously-resolved data —
// which may not round-trip accurately through a charges response
// anchored on the other side's start date.
//
// Compared to `buildBasketUpdatesFromCharges`, this helper:
//
//   - Never nulls out the non-target side's meta
//   - Writes `hire_start_date` + `hire_end_date` from the target side
//   - Updates linked children's `max_quantity` to reflect the new
//     side, but leaves their price / delivery fields untouched

/**
 * Builds a `[parent, ...linked]` update payload for a fulfilment
 * switch. Only the target side's fields are written on the parent;
 * the non-target side's meta is preserved as-is *by omission* — we
 * simply don't pass those keys into `buildLineUpdate`, so the line's
 * existing delivery_* or pickup_* fields survive the merge. This is
 * what lets the user flip back to the other side without losing
 * previously-resolved dates, which wouldn't round-trip accurately
 * through a charges response anchored on the new side's start date.
 */
export const buildBasketFulfilmentSwitchUpdates = (
  line: BasketLineData,
  linked: BasketLineData[],
  charges: BrendaChargesResult,
  targetFulfilment: 'delivery' | 'pickup'
): BasketLineItemProps[] => {
  const { delivery, pickup, product, request } = charges

  // `hire_deposit` / `hire_damage_waiver` only apply to hire (bulk)
  // lines. Fulfilment switching is effectively hire-only anyway (a
  // consumable wouldn't normally reach this path), but we gate
  // defensively so a stray consumable can't pick up either field.
  const isBulk = line.attributes.meta?.product_type === 'bulk'

  // Common parent fields, written regardless of side.
  const parentMeta: Partial<BasketLineMeta> = {
    selected_fulfilment: targetFulfilment,
    price: product.price?.amount ?? line.attributes.meta.price,
    ...(isBulk && {
      hire_deposit: product.deposit?.amount ?? 0,
      hire_damage_waiver: product.damageWaiver,
    }),
    hire_start_date: request.resolvedStartDate,
    hire_end_date: request.resolvedEndDate,
  }

  if (targetFulfilment === 'pickup') {
    // Pickup side is only viable when the branch resolved cleanly.
    // If it didn't, we still commit the meta flip but without a
    // branch — the UI will prompt the user to pick one via the
    // modal.
    const hasValidPickup =
      pickup.available && pickup.branchId && pickup.branchAddress && pickup.date

    if (hasValidPickup) {
      parentMeta.pickup_branch_id = pickup.branchId
      parentMeta.pickup_branch_address = pickup.branchAddress
      parentMeta.pickup_branch_distance = pickup.branchDistance
      parentMeta.pickup_date = pickup.date
      parentMeta.max_quantity = pickup.maxQuantity
    }
  } else if (delivery.available) {
    parentMeta.delivery_date = delivery.date
    parentMeta.delivery_price = delivery.price?.amount ?? 0
    parentMeta.max_quantity = delivery.maxQuantity
  }

  const parentUpdate = buildLineUpdate(line, parentMeta)

  // Linked children: update `max_quantity` to reflect the new side's
  // stock. Everything else on the child (price, delivery fields,
  // damage waiver) stays as it was — those describe the child in its
  // own right and shouldn't drift just because the parent flipped
  // fulfilment.
  //
  // Non-mandatory children also get their quantity force-clamped
  // when the new side's stock is lower than what's currently on the
  // line. Optional add-ons don't get their own charges fetch, so the
  // parent's switch response is our only chance to pull them back
  // into a fulfillable range — otherwise the user would be left
  // carrying a qty the target side can't satisfy. Mandatory children
  // are untouched; their qty tracks the parent's and is already
  // handled by the parent quantity flow.
  const linkedUpdates = linked.flatMap((child): BasketLineItemProps[] => {
    const nextMax =
      targetFulfilment === 'pickup'
        ? (pickup.linked.find(l => l.stockCode === child.attributes.stock_code)
            ?.maxQuantity ?? 0)
        : (() => {
            const match = charges.product.addOns.find(
              a => a.stockCode === child.attributes.stock_code
            )
            return match?.delivery.available ? match.delivery.maxQuantity : 0
          })()

    const isMandatory = !!child.attributes.meta?.is_mandatory
    const currentQty = child.attributes.quantity
    // Only clamp optional children, and only downward. Floor at 1 so
    // we never dispatch a qty=0 update — a zero-stock line is left
    // in place for the user to remove explicitly.
    const nextQty =
      !isMandatory && currentQty > nextMax
        ? Math.max(1, nextMax)
        : currentQty

    const currentMax = Number(child.attributes.meta?.max_quantity)
    const maxUnchanged =
      Number.isFinite(currentMax) && currentMax === nextMax
    const qtyUnchanged = nextQty === currentQty

    if (maxUnchanged && qtyUnchanged) return []

    return [buildLineUpdate(child, { max_quantity: nextMax }, nextQty)]
  })

  return [parentUpdate, ...linkedUpdates]
}

// ---------------------------------------------------------------------------
// Optional-child availability check
// ---------------------------------------------------------------------------

/**
 * Code used for the basket-context client issue raised when an
 * optional linked child can't be fulfilled on the target side of a
 * fulfilment switch or following a qty-change date shift.
 */
export const OPTIONAL_ADDON_UNAVAILABLE_CODE = 'addOnUnavailableForFulfilment'

/**
 * Scans a parent line's linked children against a charges response and
 * returns issues for any *non-mandatory* child whose target-side stock
 * has collapsed to zero — i.e. the add-on simply can't land for the
 * requested fulfilment + dates.
 *
 * We deliberately only flag the "completely unavailable" case here:
 * a pure downward stock drop (`maxQuantity` > 0 but < current qty) is
 * still handled silently by the existing clamp in
 * `buildBasketFulfilmentSwitchUpdates`, matching the behaviour the
 * product team chose — we don't want to block the user's flow over a
 * stock adjustment they can still fulfil with fewer units.
 *
 * The zero-max case covers both:
 *
 *   - **Date invalidity** — the parent's resolved window shifted past
 *     the add-on's earliest delivery date. For delivery this surfaces
 *     as `extra.delivery.available === false` (`mapBrendaExtra` flags
 *     the later-than-parent case that way).
 *   - **Branch / fulfilment mismatch** — a pickup add-on that simply
 *     isn't stocked at the chosen branch for the requested dates.
 *     Pickup linked data carries `{ stockCode, maxQuantity }` only,
 *     so we can't distinguish a stock problem from a date problem —
 *     both read the same to the user and the same error message
 *     applies.
 *
 * Mandatory children are skipped: their qty is linked to the parent
 * and they're gated upstream of the PDP purchase modal — they can't
 * reach the basket without stock for the requested window.
 */
export const findUnavailableOptionalChildren = (
  parent: BasketLineData,
  linked: BasketLineData[],
  charges: BrendaChargesResult,
  targetFulfilment: 'delivery' | 'pickup'
): BasketRevalidationIssue[] => {
  return linked.reduce<BasketRevalidationIssue[]>((acc, child) => {
    if (child.attributes.meta?.is_mandatory) return acc

    const stockCode = child.attributes.stock_code

    const targetMax =
      targetFulfilment === 'pickup'
        ? (charges.pickup.linked.find(l => l.stockCode === stockCode)
            ?.maxQuantity ?? 0)
        : (() => {
            const match = charges.product.addOns.find(
              a => a.stockCode === stockCode
            )
            return match?.delivery.available ? match.delivery.maxQuantity : 0
          })()

    if (targetMax > 0) return acc

    acc.push({
      code: OPTIONAL_ADDON_UNAVAILABLE_CODE,
      lineId: parent.id,
      sku: stockCode,
      title: child.title,
      fulfilment: targetFulfilment,
    })

    return acc
  }, [])
}

/**
 * Builds a full line update payload (parent + linked items) for a fulfilment switch.
 * Merges the provided meta overrides into the parent line and resolves linked stock
 * via the stock resolver callback.
 */
export const buildLinesUpdate = (
  line: BasketLineData,
  allLines: BasketLineData[],
  metaOverrides: Partial<BasketLineData['attributes']['meta']>,
  resolveLinkedStock: (child: BasketLineData) => number | null | undefined
): BasketLineItemProps[] => {
  const parentUpdate: BasketLineItemProps = {
    id: line.id,
    quantity: line.attributes.quantity,
    stock_code: line.attributes.stock_code,
    meta: {
      ...line.attributes.meta,
      ...metaOverrides,
    },
  }

  const linkedUpdates = buildLinkedStockUpdates(
    line.attributes.meta,
    allLines,
    resolveLinkedStock
  )

  return [parentUpdate, ...linkedUpdates]
}

/**
 * Builds the list of all cart-line IDs that belong to a given group
 * (parent + all linked items), ready to pass into removeItems.
 */
export const buildRemoveGroup = (
  group: BasketLineGrouped
): BasketLineItemProps[] => {
  const items = [group.parent, ...group.linked]

  return items.map(line => ({
    id: line.id,
    quantity: line.attributes.quantity,
    stock_code: line.attributes.stock_code,
    meta: line.attributes.meta,
  }))
}

// ---------------------------------------------------------------------------
// Shared helpers (used by PDP and basket)
// ---------------------------------------------------------------------------

/**
 * Resolves the shipping address from a Lunar cart's included resources.
 * Returns { address1, postcode } or null if no shipping address is set.
 */
export const getCartShippingAddress = (
  cart: LunarCart | LunarBasket | null
): { address1: string; postcode: string } | null => {
  if (!cart?.included) return null

  const addressId = cart?.data?.relationships?.['shipping-address']?.data?.id

  if (!addressId) return null

  const entry = cart.included.find(
    (e: { type: string; id: string }) =>
      e.type === 'cart-addresses' && e.id === addressId
  ) as { attributes?: { line_one?: string; postcode?: string } } | undefined

  if (!entry?.attributes?.postcode) return null

  return {
    address1: entry.attributes.line_one ?? '',
    postcode: entry.attributes.postcode,
  }
}

/**
 * Checks if any cart line has a different address to the one being passed.
 * Used on the PDP to prompt the user before adding to basket.
 */
export const checkCartAddress = (
  addressData: CustomerAddressProps,
  cart: LunarCart
) => {
  if (!cart?.included) return false

  const cartAddressId =
    cart?.data?.relationships?.['shipping-address']?.data?.id

  const cartAddresses = cart.included.filter(
    (e: { type: string; id: string }) => e.type == 'cart-addresses'
  )

  const shippingAddress = cartAddresses.find(
    (e: { type: string; id: string }) => e.id == cartAddressId
  ) as { attributes?: { postcode?: string; line_one?: string } } | undefined

  return shippingAddress?.attributes?.postcode !== addressData?.postcode ||
    shippingAddress?.attributes?.line_one !== addressData?.line_1
    ? true
    : false
}

// ---------------------------------------------------------------------------
// Revalidation helpers
// ---------------------------------------------------------------------------

// Derives whether the cart has blocking issues from the revalidation response
export const getCartHasIssues = (
  revalidation: BasketRevalidation | null | undefined
): boolean => {
  return Boolean(revalidation?.hasIssues && revalidation?.issues?.length)
}

// Derives whether the cart has non-blocking changes. Returns true
// whenever auto-corrections were applied, regardless of whether
// `hasIssues` is also true — both notices can co-exist so the user
// always sees the automatic changes the system has made on their
// behalf (e.g. a mandatory add-on bumping the hire start date),
// even when a separate blocking issue is also in play.
export const getCartHasChanges = (
  revalidation: BasketRevalidation | null | undefined
): boolean => {
  return Boolean(revalidation?.hasChanges && revalidation?.changes?.length)
}

// Builds a map of cart line ID → product title for revalidation messages
export const buildTitleByLineId = (
  lineItems: BasketLineGrouped[]
): Map<string, string> => {
  const map = new Map<string, string>()

  for (const group of lineItems) {
    map.set(group.parent.id, group.parent.title)

    for (const child of group.linked) {
      map.set(child.id, child.title)
    }
  }

  return map
}

// ---------------------------------------------------------------------------
// Revalidation formatting
// ---------------------------------------------------------------------------

// Returns a human-readable label for a revalidation change field
export const formatCartFieldLabel = (field: string) => {
  switch (field) {
    case 'price':
      return 'Price'
    case 'deliveryDate':
      return 'Delivery date'
    case 'deliveryPrice':
      return 'Delivery price'
    case 'pickupDate':
      return 'Pickup date'
    case 'hireDeposit':
      return 'Deposit'
    default:
      return field
  }
}

// Formats a revalidation change value for display
export const formatCartValue = (field: string, value: unknown) => {
  // Currency fields
  if (['price', 'deliveryPrice', 'hireDeposit'].includes(field)) {
    const symbol = getCurrencySymbol('GBP')
    const num = typeof value === 'number' ? value : Number(value)

    if (!Number.isFinite(num)) return `${symbol}${String(value ?? '')}`

    return `${symbol}${formatPrice(num)}`
  }

  // Date fields
  if (field === 'deliveryDate' || field === 'pickupDate') {
    return value ? formatDate(String(value)) : ''
  }

  return String(value ?? '')
}

// Sorts revalidation change entries into a consistent display order
const fieldOrder = [
  'deliveryDate',
  'pickupDate',
  'price',
  'deliveryPrice',
  'hireDeposit',
]

export const orderedCartChanges = (
  changes: Record<string, { old: unknown; new: unknown }>
) =>
  Object.entries(changes).sort(([a], [b]) => {
    const ai = fieldOrder.indexOf(a)
    const bi = fieldOrder.indexOf(b)

    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
